import React, { useState, useEffect } from 'react';
import { XMarkIcon } from './icons/HeroIcons';
import { AISHA_API_BASE } from '@/constants';

interface PrintfulProduct {
  id: number;
  external_id: string;
  name: string;
  thumbnail_url?: string;
  mockup_urls?: string[];
  is_ignored: boolean;
  retail_price?: string;
  currency?: string;
  created_at?: string;
  sync_product_id?: number;
  vendor_item_id?: string;
  // 追加フィールド
  display_name?: string;
  display_order?: number;
  additional_profit?: number;
  shipping_surcharge?: number;
}

interface ProductFullDetails {
  sync_product: any;
  sync_variants: any[];
  variant_details: any[];
  catalog_details: any;
  shipping_info: any;
  field_descriptions: Record<string, string>;
}

interface PrintfulProductEditModalProps {
  product: PrintfulProduct;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedProduct: PrintfulProduct) => void;
}

const PrintfulProductEditModal: React.FC<PrintfulProductEditModalProps> = ({
  product,
  isOpen,
  onClose,
  onSave
}) => {
  const [editedProduct, setEditedProduct] = useState<PrintfulProduct>(product);
  const [fullDetails, setFullDetails] = useState<ProductFullDetails | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [activeTab, setActiveTab] = useState<'edit' | 'details'>('edit');

  useEffect(() => {
    setEditedProduct(product);
    if (isOpen && (product.sync_product_id || product.vendor_item_id)) {
      fetchFullDetails();
    }
  }, [product, isOpen]);

  const fetchFullDetails = async () => {
    setIsLoadingDetails(true);
    
    try {
      let apiUrl: string;
      
      // sync_product_idがある場合はそれを使用
      if (product.sync_product_id) {
        apiUrl = `${AISHA_API_BASE}/printful/sync-products/${product.sync_product_id}/full-details/`;
      } 
      // sync_product_idが無い場合はvendor_item_idをカタログ商品IDとして使用
      else if (product.vendor_item_id) {
        apiUrl = `${AISHA_API_BASE}/printful/sync-products/catalog_${product.vendor_item_id}/full-details/`;
      } 
      else {
        console.log('商品IDが設定されていません');
        setIsLoadingDetails(false);
        return;
      }
      
      const response = await fetch(apiUrl);
      const data = await response.json();
      
      if (data.success) {
        setFullDetails(data.data);
      } else {
        console.error('商品詳細取得失敗:', data.error);
      }
    } catch (error) {
      console.error('商品詳細取得エラー:', error);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleSave = async () => {
    try {
      // ここでAPIを呼び出して保存
      const response = await fetch(`${AISHA_API_BASE}/printful/goods/${product.id}/update/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          is_ignored: editedProduct.is_ignored,
          display_name: editedProduct.display_name || editedProduct.name,
          display_order: editedProduct.display_order || 0,
          additional_profit: editedProduct.additional_profit || 0,
          shipping_surcharge: editedProduct.shipping_surcharge || 0
        })
      });

      if (response.ok) {
        onSave(editedProduct);
        onClose();
      }
    } catch (error) {
      console.error('保存エラー:', error);
    }
  };

  const formatPrice = (price: any, currency?: string) => {
    if (!price) return 'N/A';
    const curr = currency || 'JPY';
    return `${curr} ${parseFloat(price).toFixed(2)}`;
  };

  const renderDetailField = (label: string, value: any, description?: string) => {
    if (value === null || value === undefined) return null;
    
    return (
      <div className="py-2 border-b border-gray-700">
        <div className="flex justify-between items-start">
          <div className="text-sm text-gray-400">
            {description || label}
          </div>
          <div className="text-sm text-white ml-4 text-right">
            {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* ヘッダー */}
        <div className="p-4 border-b border-gray-700">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-semibold text-white">商品編集</h2>
              <p className="text-sm text-gray-400 mt-1">
                DB ID: {product.id} / カタログID: {product.vendor_item_id}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* タブ */}
        <div className="flex border-b border-gray-700">
          <button
            onClick={() => setActiveTab('edit')}
            className={`px-6 py-3 font-medium ${
              activeTab === 'edit'
                ? 'text-indigo-400 border-b-2 border-indigo-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            編集
          </button>
          <button
            onClick={() => setActiveTab('details')}
            className={`px-6 py-3 font-medium ${
              activeTab === 'details'
                ? 'text-indigo-400 border-b-2 border-indigo-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            詳細情報
          </button>
        </div>

        {/* コンテンツ */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'edit' ? (
            <div className="space-y-6">
              {/* 基本情報 */}
              <div>
                <h3 className="text-lg font-medium text-indigo-300 mb-4">基本設定</h3>
                
                {/* 公開/非公開 */}
                <div className="mb-4">
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={!editedProduct.is_ignored}
                      onChange={(e) => setEditedProduct({
                        ...editedProduct,
                        is_ignored: !e.target.checked
                      })}
                      className="w-5 h-5 text-indigo-600 bg-gray-700 border-gray-600 rounded focus:ring-indigo-500"
                    />
                    <span className="text-white">商品を公開する</span>
                  </label>
                </div>

                {/* 表示名 */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    表示名
                  </label>
                  <input
                    type="text"
                    value={editedProduct.display_name || editedProduct.name}
                    onChange={(e) => setEditedProduct({
                      ...editedProduct,
                      display_name: e.target.value
                    })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                {/* 表示順序 */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    表示順序
                  </label>
                  <input
                    type="number"
                    value={editedProduct.display_order || 0}
                    onChange={(e) => setEditedProduct({
                      ...editedProduct,
                      display_order: parseInt(e.target.value) || 0
                    })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              {/* 価格設定 */}
              <div>
                <h3 className="text-lg font-medium text-indigo-300 mb-4">価格設定</h3>
                
                {/* 追加利益 */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    追加利益 (¥)
                  </label>
                  <input
                    type="number"
                    value={editedProduct.additional_profit || 0}
                    onChange={(e) => setEditedProduct({
                      ...editedProduct,
                      additional_profit: parseFloat(e.target.value) || 0
                    })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    ※ カタログ価格に追加される利益額
                  </p>
                </div>

                {/* 送料上乗せ額 */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    送料上乗せ額 (¥)
                  </label>
                  <input
                    type="number"
                    value={editedProduct.shipping_surcharge || 0}
                    onChange={(e) => setEditedProduct({
                      ...editedProduct,
                      shipping_surcharge: parseFloat(e.target.value) || 0
                    })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    ※ Printful API送料に追加される金額
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {isLoadingDetails ? (
                <div className="text-center py-8">
                  <div className="text-gray-400">詳細情報を読み込み中...</div>
                </div>
              ) : fullDetails ? (
                <>
                  {/* カタログ商品かどうかで表示を切り替え */}
                  {fullDetails.is_catalog_only ? (
                    // カタログマスタ情報の表示
                    <>
                      {/* 日本配送不可の警告を一番上に */}
                      {fullDetails.japan_availability_summary && !fullDetails.japan_availability_summary.has_japan_shipping && (
                        <div className="bg-red-900/20 border border-red-600 rounded-lg p-4 mb-4">
                          <p className="text-red-400 font-semibold">
                            ⚠️ この商品は日本への配送に対応していません
                          </p>
                        </div>
                      )}

                      {/* 配送情報（日本向け） */}
                      {fullDetails.shipping_info && fullDetails.shipping_info.length > 0 && (
                        <div>
                          <h3 className="text-lg font-medium text-indigo-300 mb-4">配送情報（日本向け）</h3>
                          <div className="bg-gray-700 rounded-lg p-4 space-y-2">
                            {fullDetails.shipping_info.map((rate: any, index: number) => (
                              <div key={index} className="pb-2 border-b border-gray-600 last:border-0">
                                <div className="flex justify-between">
                                  <span className="text-gray-400">{rate.name}</span>
                                  <span className="text-white">¥{Math.round(parseFloat(rate.rate))}</span>
                                </div>
                                <div className="text-sm text-gray-500">
                                  配送日数: {rate.minDeliveryDays}-{rate.maxDeliveryDays}日
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* 商品基本情報 */}
                      <div>
                        <h3 className="text-lg font-medium text-indigo-300 mb-4">商品基本情報（カタログマスタ）</h3>
                        <div className="bg-gray-700 rounded-lg p-4 space-y-2">
                          {renderDetailField('商品ID', fullDetails.catalog_product?.id)}
                          {renderDetailField('商品タイプ', fullDetails.catalog_product?.type)}
                          {renderDetailField('商品名', fullDetails.catalog_product?.title)}
                          {renderDetailField('ブランド', fullDetails.catalog_product?.brand || 'N/A')}
                          {renderDetailField('モデル', fullDetails.catalog_product?.model)}
                          {renderDetailField('バリアント数', fullDetails.catalog_product?.variant_count)}
                          {renderDetailField('通貨', fullDetails.catalog_product?.currency)}
                          {renderDetailField('廃盤フラグ', fullDetails.catalog_product?.is_discontinued ? '廃盤' : '販売中')}
                          {renderDetailField('平均製造時間', fullDetails.catalog_product?.avg_fulfillment_time || 'N/A')}
                          {renderDetailField('原産国', fullDetails.catalog_product?.origin_country || 'N/A')}
                        </div>
                      </div>

                      {/* 印刷技術 */}
                      {fullDetails.catalog_product?.techniques && fullDetails.catalog_product.techniques.length > 0 && (
                        <div>
                          <h3 className="text-lg font-medium text-indigo-300 mb-4">印刷技術</h3>
                          <div className="bg-gray-700 rounded-lg p-4">
                            {fullDetails.catalog_product.techniques.map((tech: any, idx: number) => (
                              <div key={idx} className="text-white">
                                {tech.display_name} {tech.is_default && '(デフォルト)'}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* 印刷位置と追加料金 */}
                      {fullDetails.catalog_product?.files && fullDetails.catalog_product.files.length > 0 && (
                        <div>
                          <h3 className="text-lg font-medium text-indigo-300 mb-4">印刷位置と追加料金</h3>
                          <div className="bg-gray-700 rounded-lg p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {fullDetails.catalog_product.files
                                .filter((file: any) => file.type !== 'preview')
                                .map((file: any, idx: number) => {
                                  // 位置の日本語訳
                                  const positionTranslations: {[key: string]: string} = {
                                    'front': '前面',
                                    'front_large': '前面（大）',
                                    'front_dtf': '前面（DTF）',
                                    'back': '背面',
                                    'back_dtf': '背面（DTF）',
                                    'sleeve_left': '左袖',
                                    'sleeve_left_dtf': '左袖（DTF）',
                                    'sleeve_right': '右袖',
                                    'sleeve_right_dtf': '右袖（DTF）',
                                    'label_inside': 'ラベル内側',
                                    'label_outside': 'ラベル外側',
                                    'chest_left': '左胸',
                                    'chest_center': '胸中央',
                                    'default': '標準位置'
                                  };
                                  
                                  const position = positionTranslations[file.type] || file.type;
                                  const price = file.additional_price ? `+¥${parseFloat(file.additional_price).toLocaleString()}` : '追加料金なし';
                                  
                                  return (
                                    <div key={idx} className="flex justify-between items-center py-2 px-3 bg-gray-600 rounded">
                                      <div>
                                        <div className="text-white font-medium">{file.title}</div>
                                        <div className="text-gray-400 text-sm">{position}</div>
                                      </div>
                                      <div className={`font-semibold ${file.additional_price ? 'text-yellow-400' : 'text-green-400'}`}>
                                        {price}
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* 価格一覧表 */}
                      {fullDetails.price_table && fullDetails.price_table.length > 0 && (
                        <div>
                          <h3 className="text-lg font-medium text-indigo-300 mb-4">
                            価格一覧表
                            {fullDetails.japan_availability_summary && (
                              <span className="ml-2 text-sm font-normal">
                                （日本配送可能: {fullDetails.japan_availability_summary.japan_available_count}/{fullDetails.japan_availability_summary.total_variants}）
                              </span>
                            )}
                          </h3>
                          {/* 追加利益の表示 */}
                          {(editedProduct.additional_profit || 0) > 0 && (
                            <div className="mb-3 p-3 bg-indigo-900/30 rounded-lg">
                              <p className="text-sm text-indigo-300">
                                追加利益設定: ¥{editedProduct.additional_profit || 0}
                              </p>
                            </div>
                          )}
                          <div className="bg-gray-700 rounded-lg p-4 overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-gray-600">
                                  <th className="text-left py-2 text-gray-400">サイズ/色</th>
                                  <th className="text-right py-2 text-gray-400">原価</th>
                                  <th className="text-right py-2 text-gray-400">販売価格</th>
                                  <th className="text-center py-2 text-gray-400">在庫</th>
                                  <th className="text-center py-2 text-gray-400">日本配送</th>
                                  <th className="text-left py-2 text-gray-400">配送可能地域</th>
                                </tr>
                              </thead>
                              <tbody>
                                {fullDetails.price_table.map((item: any, idx: number) => {
                                  const basePrice = parseFloat(item.price);
                                  const additionalProfit = editedProduct.additional_profit || 0;
                                  const sellPrice = basePrice + additionalProfit;
                                  
                                  return (
                                    <tr key={idx} className="border-b border-gray-600">
                                      <td className="py-2 text-white">
                                        {item.size}
                                        {item.color && ` - ${item.color}`}
                                      </td>
                                      <td className="text-right py-2 text-gray-400">¥{basePrice.toLocaleString()}</td>
                                      <td className="text-right py-2 text-white font-semibold">¥{sellPrice.toLocaleString()}</td>
                                      <td className="text-center py-2">
                                        <span className={item.in_stock ? 'text-green-400' : 'text-red-400'}>
                                          {item.in_stock ? '○' : '×'}
                                        </span>
                                      </td>
                                      <td className="text-center py-2">
                                        <span className={item.is_japan_available ? 'text-green-400' : 'text-gray-500'}>
                                          {item.is_japan_available ? '✓' : '-'}
                                        </span>
                                      </td>
                                      <td className="py-2 text-gray-400 text-xs">
                                        {item.availability_regions.join(', ')}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* 商品仕様 */}
                      {fullDetails.product_specs?.raw_specs && fullDetails.product_specs.raw_specs.length > 0 && (
                        <div>
                          <h3 className="text-lg font-medium text-indigo-300 mb-4">
                            商品仕様（商品説明 / Product Description）
                          </h3>
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {/* オリジナル（英語） */}
                            <div className="bg-gray-700 rounded-lg p-4">
                              <h4 className="text-md font-medium text-gray-300 mb-3">商品仕様（オリジナル）</h4>
                              <div className="space-y-2">
                                {fullDetails.product_specs.raw_specs.map((spec: string, idx: number) => (
                                  <div key={idx} className="text-white text-sm pb-1 border-b border-gray-600 last:border-0">
                                    {spec}
                                  </div>
                                ))}
                              </div>
                            </div>
                            
                            {/* 日本語訳 */}
                            <div className="bg-gray-700 rounded-lg p-4">
                              <h4 className="text-md font-medium text-gray-300 mb-3">商品仕様（日本語）</h4>
                              <div className="space-y-2">
                                {(fullDetails.product_specs.translated_specs || fullDetails.product_specs.raw_specs).map((spec: string, idx: number) => (
                                  <div key={idx} className="text-white text-sm pb-1 border-b border-gray-600 last:border-0">
                                    {spec}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    // 既存のSync Product情報の表示
                    <>
                      <div>
                        <h3 className="text-lg font-medium text-indigo-300 mb-4">Sync Product情報</h3>
                        <div className="bg-gray-700 rounded-lg p-4 space-y-2">
                          {renderDetailField('ID', fullDetails.sync_product?.id, 'Sync Product ID / 同期商品ID')}
                          {renderDetailField('External ID', fullDetails.sync_product?.external_id, 'External ID / 外部ID')}
                          {renderDetailField('Name', fullDetails.sync_product?.name, 'Product Name / 商品名')}
                          {renderDetailField('Synced', fullDetails.sync_product?.synced, 'Synced Variants / 同期済みバリアント数')}
                          {renderDetailField('Is Ignored', fullDetails.sync_product?.is_ignored, 'Is Ignored / 非公開フラグ')}
                        </div>
                      </div>

                      {/* Catalog Product情報 */}
                      {Object.keys(fullDetails.catalog_details || {}).length > 0 && (
                        <div>
                      <h3 className="text-lg font-medium text-indigo-300 mb-4">Catalog Product情報</h3>
                      {Object.entries(fullDetails.catalog_details).map(([productId, details]: [string, any]) => (
                        <div key={productId} className="bg-gray-700 rounded-lg p-4 space-y-2 mb-4">
                          {renderDetailField('Type', details?.type, 'Product Type / 商品タイプ')}
                          {renderDetailField('Brand', details?.brand, 'Brand / ブランド')}
                          {renderDetailField('Model', details?.model, 'Model / モデル')}
                          {renderDetailField('Currency', details?.currency, 'Currency / 通貨')}
                          {renderDetailField('Variant Count', details?.variant_count, 'Variant Count / バリアント数')}
                          {renderDetailField('Discontinued', details?.is_discontinued, 'Discontinued / 廃盤')}
                          {renderDetailField('Avg Fulfillment Time', details?.avg_fulfillment_time, 'Avg Fulfillment Time / 平均製造時間')}
                          {renderDetailField('Origin Country', details?.origin_country, 'Origin Country / 原産国')}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Variant詳細 */}
                  {fullDetails.variant_details && fullDetails.variant_details.length > 0 && (
                    <div>
                      <h3 className="text-lg font-medium text-indigo-300 mb-4">Variant詳細</h3>
                      {fullDetails.variant_details.map((variant: any, index: number) => (
                        <div key={index} className="bg-gray-700 rounded-lg p-4 space-y-2 mb-4">
                          <h4 className="text-md font-medium text-white mb-2">
                            {variant.sync_variant?.name || `Variant ${index + 1}`}
                          </h4>
                          
                          {/* Sync Variant情報 */}
                          <div className="pl-4">
                            {renderDetailField('Variant ID', variant.sync_variant?.variant_id, 'Variant ID / バリアントID')}
                            {renderDetailField('Size', variant.sync_variant?.size || variant.catalog_variant?.size, 'Size / サイズ')}
                            {renderDetailField('Color', variant.sync_variant?.color || variant.catalog_variant?.color, 'Color / 色')}
                            {renderDetailField('Color Code', variant.catalog_variant?.color_code, 'Color Code / カラーコード')}
                            {renderDetailField('Retail Price', formatPrice(variant.sync_variant?.retail_price, variant.sync_variant?.currency), 'Retail Price / 販売価格')}
                            {renderDetailField('Base Price', formatPrice(variant.catalog_variant?.price, 'USD'), 'Base Price / 基本価格')}
                            {renderDetailField('In Stock', variant.catalog_variant?.in_stock, 'In Stock / 在庫有無')}
                            {renderDetailField('SKU', variant.sync_variant?.sku, 'SKU / 在庫管理番号')}
                          </div>

                          {/* Material情報 */}
                          {variant.catalog_variant?.material && variant.catalog_variant.material.length > 0 && (
                            <div className="pl-4">
                              <div className="text-sm text-gray-400 mt-2">Material / 素材:</div>
                              {variant.catalog_variant.material.map((mat: any, idx: number) => (
                                <div key={idx} className="text-sm text-white ml-4">
                                  {mat.name}: {mat.percentage}%
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* 配送情報 */}
                  {fullDetails.shipping_info && (
                    <div>
                      <h3 className="text-lg font-medium text-indigo-300 mb-4">配送情報（日本向け）</h3>
                      <div className="bg-gray-700 rounded-lg p-4 space-y-2">
                        {fullDetails.shipping_info.result?.map((rate: any, index: number) => (
                          <div key={index} className="pb-2 border-b border-gray-600 last:border-0">
                            <div className="flex justify-between">
                              <span className="text-gray-400">{rate.name}</span>
                              <span className="text-white">¥{Math.round(parseFloat(rate.rate))}</span>
                            </div>
                            <div className="text-sm text-gray-500">
                              配送日数: {rate.minDeliveryDays}-{rate.maxDeliveryDays}日
                            </div>
                          </div>
                        ))}
                      </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-400">詳細情報の取得に失敗しました</div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* フッター */}
        <div className="p-4 border-t border-gray-700 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            キャンセル
          </button>
          {activeTab === 'edit' && (
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
            >
              保存
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrintfulProductEditModal;